import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageSquare,
  TrendingUp,
  Users,
  Clock,
  Target,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Download,
  Filter,
  Calendar,
  BarChart3,
  PieChart,
  Activity,
  Zap,
  Globe,
  Smartphone,
  Phone,
  Mail,
  Eye,
  EyeOff,
  MousePointer,
  Reply,
  DollarSign,
  Percent,
  TrendingDown,
  ArrowUp,
  ArrowDown,
  PlayCircle,
  PauseCircle,
  Settings,
  Info,
  Send,
  Signal
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button/Button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/Select';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  TimeScale,
  Filler
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { useTheme } from '../../hooks/useTheme';
import { useWebSocket } from '../../hooks/useWebSocket';
import { cn } from '../../utils/cn';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

interface SMSAnalyticsDashboardProps {
  campaignId: string;
  className?: string;
}

interface SMSMetrics {
  totalSent: number;
  delivered: number;
  failed: number;
  replies: number;
  optOuts: number;
  deliveryRate: number;
  replyRate: number;
  optOutRate: number;
  engagementScore: number;
  averageResponseTime: number;
  conversationThreads: number;
  totalCost: number;
  costPerMessage: number;
  costPerReply: number;
}

interface RealTimeMetrics {
  timestamp: Date;
  sent: number;
  delivered: number;
  failed: number;
  replies: number;
  optOuts: number;
  deliveryVelocity: number;
  replyVelocity: number;
}

interface CarrierPerformance {
  carrier: string;
  sent: number;
  delivered: number;
  failed: number;
  deliveryRate: number;
  avgDeliveryTime: number;
  status: 'healthy' | 'degraded' | 'critical';
}

interface Recommendation {
  category: 'content' | 'timing' | 'targeting' | 'frequency' | 'compliance';
  priority: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  description: string;
  expectedImpact: string;
  actionSteps: string[];
  effort: 'low' | 'medium' | 'high';
}

const SMSAnalyticsDashboard: React.FC<SMSAnalyticsDashboardProps> = ({
  campaignId,
  className = ''
}) => {
  const [metrics, setMetrics] = useState<SMSMetrics | null>(null);
  const [realTimeMetrics, setRealTimeMetrics] = useState<RealTimeMetrics | null>(null);
  const [carrierPerformance, setCarrierPerformance] = useState<CarrierPerformance[]>([]);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState('24h');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedView, setSelectedView] = useState<'overview' | 'performance' | 'engagement' | 'carriers'>('overview');

  // Fetch comprehensive analytics
  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      
      // Handle demo campaigns by providing mock data
      if (campaignId.startsWith('demo-')) {
        const mockMetrics: SMSMetrics = {
          totalSent: 15420,
          delivered: 14890,
          failed: 530,
          replies: 892,
          optOuts: 43,
          deliveryRate: 0.966,
          replyRate: 0.058,
          optOutRate: 0.003,
          engagementScore: 85.2,
          averageResponseTime: 12.5,
          conversationThreads: 678,
          totalCost: 462.60,
          costPerMessage: 0.03,
          costPerReply: 0.52
        };

        const mockCarrierPerformance: CarrierPerformance[] = [
          {
            carrier: 'Verizon',
            sent: 4820,
            delivered: 4680,
            failed: 140,
            deliveryRate: 0.971,
            avgDeliveryTime: 2.3,
            status: 'healthy'
          },
          {
            carrier: 'AT&T',
            sent: 4150,
            delivered: 4010,
            failed: 140,
            deliveryRate: 0.966,
            avgDeliveryTime: 2.8,
            status: 'healthy'
          },
          {
            carrier: 'T-Mobile',
            sent: 3890,
            delivered: 3720,
            failed: 170,
            deliveryRate: 0.956,
            avgDeliveryTime: 3.1,
            status: 'degraded'
          },
          {
            carrier: 'Sprint',
            sent: 2560,
            delivered: 2480,
            failed: 80,
            deliveryRate: 0.969,
            avgDeliveryTime: 2.6,
            status: 'healthy'
          }
        ];

        const mockRecommendations: Recommendation[] = [
          {
            category: 'timing',
            priority: 'high',
            title: 'Optimize Send Times',
            description: 'Analysis shows 23% higher reply rates when messages are sent between 10-11 AM on weekdays.',
            expectedImpact: '+15% reply rate improvement',
            actionSteps: [
              'Schedule messages for 10:00-11:00 AM window',
              'Avoid weekend sends for business audiences',
              'Test Tuesday-Thursday as optimal days'
            ],
            effort: 'low'
          },
          {
            category: 'content',
            priority: 'medium',
            title: 'Improve Message Personalization',
            description: 'Messages with recipient names show 31% better engagement than generic messages.',
            expectedImpact: '+20% engagement boost',
            actionSteps: [
              'Add first name personalization',
              'Include company-specific details',
              'Reference recent interactions'
            ],
            effort: 'medium'
          },
          {
            category: 'frequency',
            priority: 'low',
            title: 'Adjust Follow-up Cadence',
            description: 'Current 3-day follow-up interval could be reduced to 2 days for better response rates.',
            expectedImpact: '+8% response rate increase',
            actionSteps: [
              'Test 2-day follow-up interval',
              'Monitor opt-out rates',
              'A/B test different cadences'
            ],
            effort: 'low'
          }
        ];

        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 800));
        
        setMetrics(mockMetrics);
        setCarrierPerformance(mockCarrierPerformance);
        setRecommendations(mockRecommendations);
        setError(null);
        setLoading(false);
        return;
      }

      const endDate = new Date();
      const startDate = new Date();
      
      switch (timeRange) {
        case '1h':
          startDate.setHours(startDate.getHours() - 1);
          break;
        case '24h':
          startDate.setDate(startDate.getDate() - 1);
          break;
        case '7d':
          startDate.setDate(startDate.getDate() - 7);
          break;
        case '30d':
          startDate.setDate(startDate.getDate() - 30);
          break;
      }

      const response = await fetch(
        `/api/analytics/sms/campaigns/${campaignId}/comprehensive?` +
        `startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}&includePredictions=true`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch analytics');
      }

      const data = await response.json();
      setMetrics(data.data.summary);
      setCarrierPerformance(data.data.deliveryAnalysis.carrierPerformance);
      setRecommendations(data.data.recommendations);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  // Fetch real-time metrics
  const fetchRealTimeMetrics = async () => {
    try {
      // Handle demo campaigns with mock real-time data
      if (campaignId.startsWith('demo-')) {
        const mockRealTimeMetrics: RealTimeMetrics = {
          timestamp: new Date(),
          sent: Math.floor(Math.random() * 10) + 5,
          delivered: Math.floor(Math.random() * 8) + 3,
          failed: Math.floor(Math.random() * 2),
          replies: Math.floor(Math.random() * 3),
          optOuts: Math.floor(Math.random() * 1),
          deliveryVelocity: Math.random() * 5 + 2,
          replyVelocity: Math.random() * 2 + 0.5
        };
        
        setRealTimeMetrics(mockRealTimeMetrics);
        return;
      }

      const response = await fetch(`/api/analytics/sms/campaigns/${campaignId}/realtime`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setRealTimeMetrics(data.data.liveUpdates);
      }
    } catch (err) {
      console.error('Failed to fetch real-time metrics:', err);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [campaignId, timeRange]);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(fetchRealTimeMetrics, 30000); // Every 30 seconds
      return () => clearInterval(interval);
    }
  }, [autoRefresh, campaignId]);

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatPercentage = (value: number): string => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'text-green-400';
      case 'degraded': return 'text-yellow-400';
      case 'critical': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'high': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'medium': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'low': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 ${className}`}>
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center space-x-3">
            <RefreshCw className="w-6 h-6 text-blue-400 animate-spin" />
            <span className="text-white text-lg">Loading SMS Analytics...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 ${className}`}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">Error Loading Analytics</h3>
            <p className="text-gray-300 mb-4">{error}</p>
            <button
              onClick={fetchAnalytics}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!metrics) return null;

  return (
    <div className={`min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 ${className}`}>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">SMS Analytics Dashboard</h1>
            <p className="text-gray-300">Campaign: {campaignId}</p>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Time Range Selector */}
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="bg-gray-800/50 border border-gray-700 text-white px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="1h">Last Hour</option>
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
            </select>

            {/* Auto Refresh Toggle */}
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                autoRefresh 
                  ? 'bg-green-600/20 text-green-400 border border-green-500/30' 
                  : 'bg-gray-800/50 text-gray-400 border border-gray-700'
              }`}
            >
              {autoRefresh ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              <span>Auto Refresh</span>
            </button>

            {/* Manual Refresh */}
            <button
              onClick={fetchAnalytics}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* View Selector */}
        <div className="flex space-x-2 bg-gray-800/30 p-1 rounded-lg">
          {[
            { key: 'overview', label: 'Overview', icon: BarChart3 },
            { key: 'performance', label: 'Performance', icon: TrendingUp },
            { key: 'engagement', label: 'Engagement', icon: Users },
            { key: 'carriers', label: 'Carriers', icon: Signal }
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setSelectedView(key as any)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                selectedView === key
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-300 hover:text-white hover:bg-gray-700/50'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
            </button>
          ))}
        </div>

        {/* Real-time Metrics Bar */}
        {realTimeMetrics && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 backdrop-blur-sm border border-blue-500/30 rounded-xl p-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Activity className="w-5 h-5 text-blue-400" />
                <span className="text-white font-medium">Live Metrics</span>
                <span className="text-xs text-gray-400">
                  Updated: {new Date(realTimeMetrics.timestamp).toLocaleTimeString()}
                </span>
              </div>
              
              <div className="flex items-center space-x-6">
                <div className="text-center">
                  <div className="text-sm text-gray-400">Delivery Velocity</div>
                  <div className="text-lg font-semibold text-green-400">
                    {realTimeMetrics.deliveryVelocity.toFixed(1)}/min
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-gray-400">Reply Velocity</div>
                  <div className="text-lg font-semibold text-blue-400">
                    {realTimeMetrics.replyVelocity.toFixed(1)}/min
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-gray-400">Active Now</div>
                  <div className="text-lg font-semibold text-purple-400">
                    {realTimeMetrics.sent + realTimeMetrics.delivered + realTimeMetrics.replies}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Overview View */}
        {selectedView === 'overview' && (
          <div className="space-y-6">
            {/* Key Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  title: 'Total Sent',
                  value: formatNumber(metrics.totalSent),
                  icon: Send,
                  color: 'blue',
                  trend: '+12%'
                },
                {
                  title: 'Delivery Rate',
                  value: formatPercentage(metrics.deliveryRate),
                  icon: CheckCircle,
                  color: 'green',
                  trend: '+2.3%'
                },
                {
                  title: 'Reply Rate',
                  value: formatPercentage(metrics.replyRate),
                  icon: Reply,
                  color: 'purple',
                  trend: '+5.1%'
                },
                {
                  title: 'Total Cost',
                  value: formatCurrency(metrics.totalCost),
                  icon: DollarSign,
                  color: 'orange',
                  trend: '-8%'
                }
              ].map((metric, index) => (
                <motion.div
                  key={metric.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6 hover:border-gray-600 transition-colors"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className={`p-3 rounded-lg bg-${metric.color}-500/20`}>
                      <metric.icon className={`w-6 h-6 text-${metric.color}-400`} />
                    </div>
                    <div className={`text-sm font-medium ${
                      metric.trend.startsWith('+') ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {metric.trend}
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-white mb-1">{metric.value}</div>
                  <div className="text-sm text-gray-400">{metric.title}</div>
                </motion.div>
              ))}
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Delivery Performance Chart */}
              <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Delivery Performance</h3>
                <div className="h-64">
                  <Doughnut
                    data={{
                      labels: ['Delivered', 'Failed', 'Pending'],
                      datasets: [{
                        data: [metrics.delivered, metrics.failed, metrics.totalSent - metrics.delivered - metrics.failed],
                        backgroundColor: ['#10B981', '#EF4444', '#6B7280'],
                        borderWidth: 0
                      }]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'bottom',
                          labels: { color: '#D1D5DB' }
                        }
                      }
                    }}
                  />
                </div>
              </div>

              {/* Engagement Metrics */}
              <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Engagement Metrics</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300">Engagement Score</span>
                    <span className="text-xl font-semibold text-purple-400">
                      {metrics.engagementScore.toFixed(1)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300">Avg Response Time</span>
                    <span className="text-lg text-blue-400">
                      {Math.round(metrics.averageResponseTime)}m
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300">Conversation Threads</span>
                    <span className="text-lg text-green-400">
                      {formatNumber(metrics.conversationThreads)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300">Cost per Reply</span>
                    <span className="text-lg text-orange-400">
                      {formatCurrency(metrics.costPerReply)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Carrier Performance View */}
        {selectedView === 'carriers' && (
          <div className="space-y-6">
            <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Carrier Performance</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-3 px-4 text-gray-300">Carrier</th>
                      <th className="text-left py-3 px-4 text-gray-300">Sent</th>
                      <th className="text-left py-3 px-4 text-gray-300">Delivered</th>
                      <th className="text-left py-3 px-4 text-gray-300">Failed</th>
                      <th className="text-left py-3 px-4 text-gray-300">Delivery Rate</th>
                      <th className="text-left py-3 px-4 text-gray-300">Avg Time</th>
                      <th className="text-left py-3 px-4 text-gray-300">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {carrierPerformance.map((carrier, index) => (
                      <motion.tr
                        key={carrier.carrier}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="border-b border-gray-800 hover:bg-gray-700/30"
                      >
                        <td className="py-3 px-4 text-white font-medium">{carrier.carrier}</td>
                        <td className="py-3 px-4 text-gray-300">{formatNumber(carrier.sent)}</td>
                        <td className="py-3 px-4 text-green-400">{formatNumber(carrier.delivered)}</td>
                        <td className="py-3 px-4 text-red-400">{formatNumber(carrier.failed)}</td>
                        <td className="py-3 px-4 text-blue-400">{formatPercentage(carrier.deliveryRate)}</td>
                        <td className="py-3 px-4 text-purple-400">{carrier.avgDeliveryTime.toFixed(1)}m</td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(carrier.status)}`}>
                            {carrier.status}
                          </span>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Recommendations */}
        {recommendations.length > 0 && (
          <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <Target className="w-5 h-5 mr-2 text-blue-400" />
              Recommendations
            </h3>
            <div className="space-y-4">
              {recommendations.slice(0, 3).map((rec, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`p-4 rounded-lg border ${getPriorityColor(rec.priority)}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-semibold">{rec.title}</h4>
                    <span className="text-xs px-2 py-1 rounded-full bg-gray-700 text-gray-300">
                      {rec.priority}
                    </span>
                  </div>
                  <p className="text-sm text-gray-300 mb-3">{rec.description}</p>
                  <div className="text-sm">
                    <span className="font-medium text-green-400">Expected Impact: </span>
                    <span className="text-gray-300">{rec.expectedImpact}</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SMSAnalyticsDashboard; 